/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createActivity,
  editActivity,
  getActivities,
  getActivitiesLookups,
} from "../activitiesService";
import { CreateOrUpdateActivityPayload } from "../types/types";

enum TimeInMilliseconds {
  Hour = 60 * 60 * 1000,
  Day = 60 * 60 * 24 * 1000,
  Week = 7 * 60 * 60 * 24 * 1000,
}

export const useActivities = (accountId: string) => {
  return useQuery({
    queryKey: ["activities", accountId],
    queryFn: async () => {
      const response = await getActivities(accountId);
      return response.data;
    },
    staleTime: 0,
    gcTime: TimeInMilliseconds.Hour,
  });
};

export const useActivitiesLookups = () => {
  return useQuery({
    queryKey: ["activities-lookups"],
    queryFn: async () => {
      const response = await getActivitiesLookups();
      return response.data;
    },
    staleTime: TimeInMilliseconds.Day,
    gcTime: TimeInMilliseconds.Week,
  });
};

export const useCreateActivity = (accountId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateOrUpdateActivityPayload) => {
      const response = await createActivity(payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", accountId] });
    },
  });
};

export const useUpdateActivity = (accountId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateOrUpdateActivityPayload) => {
      const response = await editActivity(payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", accountId] });
    },
  });
};
